import { useState } from "react";
import { LockKeyhole, ShieldCheck } from "lucide-react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { adminCredentials, useAdminAuth } from "@/admin/auth/AdminAuthContext";
import { PanelCard } from "@/admin/components/PanelCard";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAdminAuth();
  const [email, setEmail] = useState(adminCredentials.email);
  const [password, setPassword] = useState(adminCredentials.password);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const redirectTo = location.state?.from?.pathname || "/admin/dashboard";

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      await login({ email, password });
      navigate(redirectTo, { replace: true });
    } catch (loginError) {
      setError(loginError.message || "Login failed.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="admin-shell admin-grid-pattern flex min-h-screen items-center justify-center px-4 py-10">
      <div className="grid w-full max-w-6xl gap-6 lg:grid-cols-[1.1fr_minmax(380px,0.8fr)]">
        <PanelCard className="hidden overflow-hidden lg:block">
          <div className="flex h-full flex-col justify-between bg-[linear-gradient(140deg,#0b1221_0%,#0033ff_58%,#dbe7ff_100%)] p-10 text-white">
            <div>
              <span className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/84">
                Secure admin access
              </span>
              <h1 className="mt-8 max-w-xl font-display text-5xl font-extrabold tracking-tight">
                Run QuickDropGo operations from a single, focused command panel.
              </h1>
              <p className="mt-6 max-w-lg text-base leading-8 text-white/78">
                Track active deliveries, monitor partner health, and respond to live dispatch changes with a clean blue-and-white workspace built for fast decisions.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-[28px] bg-white/12 p-5 backdrop-blur-sm">
                <ShieldCheck className="h-6 w-6" />
                <p className="mt-5 text-lg font-semibold">Protected routes</p>
                <p className="mt-2 text-sm leading-6 text-white/76">
                  Every admin route is gated behind session-aware navigation.
                </p>
              </div>
              <div className="rounded-[28px] bg-white/12 p-5 backdrop-blur-sm">
                <LockKeyhole className="h-6 w-6" />
                <p className="mt-5 text-lg font-semibold">Secure sign-in flow</p>
                <p className="mt-2 text-sm leading-6 text-white/76">
                  Local session state with explicit login and logout handling.
                </p>
              </div>
            </div>
          </div>
        </PanelCard>

        <PanelCard className="p-6 sm:p-8">
          <div className="rounded-[26px] bg-[#f5f9ff] p-5">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#5f79c4]">
              QuickDropGo Admin
            </p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-[#0b1221]">
              Sign in to continue
            </h2>
            <p className="mt-3 text-sm leading-7 text-[#6c7c98]">
              Use the admin credentials below to access the dashboard.
            </p>
          </div>

          <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-[#1e325d]">Email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="h-13 w-full rounded-2xl border border-[#d9e5ff] bg-white px-4 py-3 text-sm text-[#0b1221] outline-none transition focus:border-[#7ca4ff] focus:ring-4 focus:ring-[#dbe7ff]"
                autoComplete="username"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-[#1e325d]">Password</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="h-13 w-full rounded-2xl border border-[#d9e5ff] bg-white px-4 py-3 text-sm text-[#0b1221] outline-none transition focus:border-[#7ca4ff] focus:ring-4 focus:ring-[#dbe7ff]"
                autoComplete="current-password"
              />
            </label>

            {adminCredentials.email ? (
              <div className="rounded-2xl border border-dashed border-[#c8d8ff] bg-[#f8fbff] px-4 py-3 text-sm text-[#49638b]">
                Authorized admin email: {adminCredentials.email}
              </div>
            ) : null}

            {error ? (
              <div className="rounded-2xl border border-[#ffd0d0] bg-[#fff4f4] px-4 py-3 text-sm text-[#b42318]">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex w-full items-center justify-center rounded-2xl bg-[#0033ff] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_16px_30px_rgba(0,51,255,0.24)] transition hover:bg-[#002ce0] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? "Signing in..." : "Enter admin panel"}
            </button>
          </form>
        </PanelCard>
      </div>
    </div>
  );
}